import React, { useState } from 'react';
import { Download, AlertCircle, RefreshCw, Eye } from 'lucide-react';
import { ImageLightbox } from './ImageLightbox';
import { formatFileSize } from './attachmentUtils';
import { usePresignedFileUrl } from './usePresignedFileUrl';

interface ImageAttachmentProps {
  fileName: string;
  url: string;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
}

export const ImageAttachment: React.FC<ImageAttachmentProps> = ({
  fileName,
  url,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [renderError, setRenderError] = useState(false);
  const retryCountRef = React.useRef(0);
  const MAX_AUTO_RETRIES = 2;

  const presigned = usePresignedFileUrl(workspaceId, fileId);

  const displayUrl = presigned.url || url;
  const isLoading = presigned.isLoading;
  const hasError = presigned.hasError || renderError;

  const backendDownloadUrl = (workspaceId && fileId)
    ? `/api/v1/workspaces/${workspaceId}/files/${fileId}/download?disposition=attachment`
    : displayUrl;

  const handleImageError = React.useCallback(() => {
    if (retryCountRef.current < MAX_AUTO_RETRIES && workspaceId && fileId) {
      retryCountRef.current += 1;
      console.info('[ImageAttachment] Image load error — force refreshing presigned URL, attempt', retryCountRef.current);
      setRenderError(false);
      presigned.refresh(true);
    } else {
      setRenderError(true);
    }
  }, [workspaceId, fileId, presigned]);

  const handleRetry = async () => {
    retryCountRef.current = 0;
    setRenderError(false);
    await presigned.refresh(true);
  };

  return (
    <div className="my-2 max-w-md select-none group/img">
      {hasError ? (
        /* Fallback box on preview load error or expired presigned URL */
        <div className="p-3.5 rounded-2xl bg-card border border-border flex items-center justify-between gap-3 text-xs shadow-sm">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate">{fileName}</p>
              <p className="text-[11px] text-muted-foreground">
                Preview unavailable • {formatFileSize(fileSize || presigned.fileSize)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={handleRetry}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
              title="Retry preview"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <a
              href={backendDownloadUrl}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg hover:bg-primary/10 text-primary transition font-medium"
              title="Download image"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-4 w-4" />
            </a>
          </div>
        </div>
      ) : (
        /* Image Preview Box */
        <div className="relative rounded-2xl border border-border/80 bg-card overflow-hidden shadow-sm transition hover:shadow-md max-w-full">
          {isLoading && (
            <div className="h-48 w-full bg-muted/40 animate-pulse flex items-center justify-center text-xs text-muted-foreground">
              Loading image...
            </div>
          )}

          {!isLoading && displayUrl && (
            <img
              src={displayUrl}
              alt={fileName}
              onError={handleImageError}
              onClick={() => setIsLightboxOpen(true)}
              className="max-h-96 w-auto max-w-full object-cover cursor-pointer transition duration-300 block group-hover/img:scale-[1.01]"
            />
          )}

          {/* Bottom Info & Action Overlay */}
          {!isLoading && (
            <div className="flex items-center justify-between px-3 py-1.5 bg-card/90 backdrop-blur-xs border-t border-border/60 text-xs">
              <div className="min-w-0 pr-2">
                <p className="font-semibold text-foreground truncate text-[12px]">{fileName}</p>
                {(fileSize || presigned.fileSize > 0) && (
                  <p className="text-[10px] text-muted-foreground">{formatFileSize(fileSize || presigned.fileSize)}</p>
                )}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsLightboxOpen(true)}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                  title="Expand view"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <a
                  href={backendDownloadUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg hover:bg-primary/10 text-primary transition"
                  title="Download image"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Modal */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        src={displayUrl}
        alt={fileName}
        onClose={() => setIsLightboxOpen(false)}
      />
    </div>
  );
};
